/**
 * 줄 끝 ▼ 메뉴 (v2.317.0 · Salesforce 식 row action).
 *
 *   - 표 줄마다 [수정] [삭제] … 단추를 칸마다 두는 대신 **▼ 한 칸**에 모은다 → 칸 폭이 돌아온다.
 *   - 누르면 아래(자리가 없으면 위)로 작은 목록이 뜬다. 바깥 클릭·ESC·스크롤이면 닫힌다.
 *   - 목록은 body 에 그린다(createPortal) — 표의 overflow 에 잘리지 않게.
 *   - 줄 클릭으로 상세가 열리는 표에서는 단추가 줄 클릭을 삼킨다(stopPropagation).
 *   - 항목: label · onClick · tone('danger' 면 빨간 글자) · disabled · title.
 */
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';

export interface RowMenuItem {
  label: string;
  onClick: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  title?: string;
}

export function RowMenu({
  items,
  label = '더 보기',
  className = '',
}: {
  items: RowMenuItem[];
  /** 단추의 보조 설명(접근성) */
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuH = Math.min(items.length, 8) * 32 + 8;
    const below = r.bottom + menuH + 8 <= window.innerHeight;
    setPos({ top: below ? r.bottom + 4 : Math.max(8, r.top - menuH - 4), left: Math.min(r.right - 160, window.innerWidth - 168) });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const usable = items.filter(Boolean);
  if (usable.length === 0) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e: ReactMouseEvent) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={`btn btn-ghost btn-sm btn-icon text-slate-500 ${open ? 'bg-slate-100 text-slate-900' : ''} ${className}`}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[130] min-w-[160px] rounded border border-slate-200 bg-white py-1 shadow-md"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {usable.map((it, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                title={it.title}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  it.onClick();
                }}
                className={`block w-full px-3 py-1.5 text-left text-[13px] leading-5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 ${
                  it.tone === 'danger' ? 'text-red-600' : 'text-slate-900'
                }`}
              >
                {it.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
